// Puente entre los design tokens (design-tokens.scss) y recharts, que pinta
// atributos SVG donde var(--token) NO resuelve: hay que entregarle colores
// concretos. Mismo mecanismo que DeliveryAnalytics.jsx (tokenRGB + isDark).

import { useMemo } from "react";
import useTheme from "../../../../hooks/useTheme";

/**
 * Lee un triplete RGB de un token ("249 115 22") y lo envuelve en rgb(...).
 * Fallback hex para SSR/tests, donde no hay getComputedStyle.
 * @param {string} name - nombre de la variable CSS (con --)
 * @param {string} fallback - color hex si el token no existe
 * @returns {string}
 */
const tokenRGB = (name, fallback) => {
  if (typeof window === "undefined" || !window.getComputedStyle) return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v ? `rgb(${v.replace(/\s+/g, ", ")})` : fallback;
};

/**
 * Colores y props de recharts derivados de los tokens del tema vigente.
 * Se leen UNA vez por cambio de tema (dep isDark): getComputedStyle en cada
 * render forzaría reflow, y los objetos memoizados mantienen identidad estable
 * entre renders — regla PERF del helpdesk (nada de literales nuevos por render).
 * @returns {{colors: object, axisTick: object, gridProps: object, tooltipStyle: object, tooltipLabelStyle: object}}
 */
export function useChartColors() {
  const { isDark } = useTheme();

  return useMemo(() => {
    const colors = {
      // En dark la línea baja a brand-400: el 500 sobre fondo casi negro vibra
      // (mismo criterio que DeliveryAnalytics).
      brand: tokenRGB(isDark ? "--brand-400" : "--brand-500", "#F97316"),
      info: tokenRGB("--status-info", "#0084FF"),
      fg: tokenRGB("--fg", "#171717"),
      fgMuted: tokenRGB("--fg-muted", "#666666"),
      border: tokenRGB("--border", "#E3E3E3"),
      bg: tokenRGB("--bg", "#FFFFFF"),
      bgMuted: tokenRGB("--bg-muted", "#FAFAFA"),
    };

    return {
      colors,
      // Ticks de ejes: 11px es el mínimo tipográfico del helpdesk; fg-muted
      // cumple AA y deja el protagonismo a las series.
      axisTick: { fontSize: 11, fill: colors.fgMuted },
      // Grid recesivo: solo líneas horizontales, punteadas, color border.
      gridProps: { stroke: colors.border, strokeDasharray: "3 3", vertical: false },
      tooltipStyle: {
        background: colors.bg,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        fontSize: 12,
      },
      tooltipLabelStyle: { color: colors.fg, fontSize: 12, fontWeight: 600 },
    };
  }, [isDark]);
}

export default useChartColors;
